import { lazy, Suspense, type ReactElement, type ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import * as Y from 'yjs'
import { HocuspocusProvider } from '@hocuspocus/provider'
import type { Awareness } from 'y-protocols/awareness'
import { useLang } from '../i18n'
import { api, isApiError } from '../lib/api'
import { textStats, type TextStats } from '../lib/text-stats'
import type { PartKind, Revision, HistoryEntry, VersionMeta } from '../lib/types'
import type { ArtefactType } from '../lib/artefact-type'
import type { Editor } from '@tiptap/react'
import { RichEditor } from './editor/RichEditor'
import { GridEditor } from './grid/GridEditor'
import { EditorToolbar } from './editor/EditorToolbar'
import type { SectionLock } from './editor/SectionNumbers'
import { SectionOutline } from './SectionOutline'
import { ExportMenu } from './ExportMenu'
import { AgentChip } from './AgentAvatar'
import { IconContract, IconExpand } from './NavIcons'
import { HistoryModal, type VersionPatch } from './HistoryModal'
import { FindReplace } from './FindReplace'
import { useFindStore } from '../lib/findStore'
import { IconSearch } from './editor/ToolbarIcons'
import type { DeckFindApi } from '../slides/find'
import { Toasts, type Toast } from './Toasts'
import { LockNotices, useLockNotices } from './LockNotices'
import { useHistoryCapture } from './editor/useHistoryCapture'
import { appendHistory, makeHistoryEntry } from './editor/history-log'
import { reconstructAt, reconstructableIds, restoreCopyName } from '../lib/history-restore'
import { useRoomVersions } from '../lib/use-room-versions'
import { reportError } from '../lib/errors'
import { isOutdatedRefusal, markClientOutdated, roomParameters, useClientOutdated } from '../lib/client-floor'
import { isRoomRestored, noteRoomEpoch } from '../lib/room-epoch'
import { isAgentBusy } from '../lib/agent-busy'
import { useLockAfterglow, useMyUserId, useAgentActivity, isAgentLock, lockKey, lockExpired, lockLapsesAt } from '../lib/lock-view'
import { useRoomHealth, reauthenticateRoom } from '../lib/room-connection'
import { prefetchDeckTemplates } from '../slides/template-query'
import { freshToken, onTokenRenewed, renewToken } from '../lib/session-token'
import { getToken } from '../lib/backend'
import { partShareUrl } from '../lib/router'
import { actorDisplayName } from '../lib/actor-name'

// The slides editor is its OWN chunk, fetched the first time a slides part opens: react-moveable
// (75 kB gzip, DEBT.md) and the slide renderer never weigh on the entry chunk every member loads.
const SlidesEditor = lazy(() => import('../slides/SlidesEditor'))

interface CanvasProps {
  room: string | null
  /**
   * What the open part CONTAINS — the only thing that decides which editor mounts on its room.
   *
   * Passed down from the outline (which reads it off `GET /v1/artefacts`) rather than sniffed from
   * the ydoc here: the room's own `Y.Map('meta')` is authoritative but arrives only AFTER the
   * first sync, so branching on it would flash the wrong editor on every open. Defaults to
   * 'markdown', which is what an older server's outline implies.
   */
  kind?: PartKind
  /** The type of the deliverable holding the part (prose by default) — decides which export
   *  formats the Export dialog offers (lib/artefact-type.ts). */
  artefactType?: ArtefactType
  wsUrl: string
  user: { name: string; color: string }
  /**
   * The caller has `viewer` access to this bid: mount the editor read-only and say why.
   *
   * Passed in rather than resolved here — the host already knows the active bid, and Canvas is
   * rendered in contexts without a QueryClient. An AFFORDANCE either way: the server refuses a
   * viewer's writes twice over (REST 403 + a read-only CRDT connection).
   */
  readOnly?: boolean
  /**
   * The PART is read-only (AB#209) — for everyone, whatever their access. The host passes
   * `readOnly` too (the editor opens exactly as it does for a viewer); this only changes the banner,
   * which names where a restored copy came from. The server refuses every content write to the part
   * (REST 423 + a read-only CRDT connection), so this is an affordance, never the enforcement.
   */
  partLock?: { restoredFrom?: { part: string; at: string } }
  /**
   * The caller has `viewer` access — the part of `readOnly` that is about the MEMBER rather than
   * the part. Separate because the two hide different History actions: a viewer gets neither, while
   * a read-only part keeps "Restore as part" (it only reads the part).
   */
  viewer?: boolean
  /** The part's display title — what a "Restore as part" copy is named after, and the second half
   *  of the header title ("Artefact / Part"). */
  partTitle?: string
  /** The artefact's display title — the first half of the header title. */
  artefactTitle?: string
  /** Open another part (the copy "Restore as part" just created). Supplied by the host, which owns
   *  routing and the outline query; omitted, the copy is created but not opened. */
  onOpenPart?: (room: string) => void
  /**
   * The caller is a BETA TESTER. Passed down like `readOnly`, and for the same reason: Canvas is
   * rendered in contexts without a QueryClient, so it cannot ask `/v1/me` itself.
   *
   * A non-tester who opens a grid part that already exists gets it READ-ONLY. Grid parts are not
   * hidden from the outline — doing that would make an export differ by viewer, which is a worse
   * problem than a beta feature being visible — but a non-tester writes nothing into one.
   */
  beta?: boolean
  /**
   * The caller is an ADMIN of this bid (`useBidProfile(bid).isAdmin`). Passed down like `beta`,
   * for the same reason. Gates "convert this table to a grid part", on top of the beta gate, and
   * the History modal's "Flatten history…" (AB#243 — a tenant admin counts as a bid admin there).
   */
  admin?: boolean
  /**
   * Focus mode (AB#282) is on: the shell has hidden its nav, breadcrumb bar and footer. Shell state
   * (App.tsx), so Canvas only draws the toggle and tells a slides editor to close its side panes.
   */
  focusMode?: boolean
  /** Press on the expand / restore button. Omitted, no button is drawn. */
  onToggleFocus?: () => void
  /** The host's "Open chat" control while the chat is collapsed (AB#338), drawn last in the top
   *  bar's right-hand cluster, right of the connection pill. Omitted, nothing is drawn. */
  chatReopen?: ReactNode
}

interface Participant {
  clientId: number
  name: string
  color: string
}

interface Conn {
  /** The room this connection is to: for one render after a part switch, `conn` is still the old one. */
  room: string
  ydoc: Y.Doc
  ytext: Y.Text
  awareness: Awareness | null
  /** The live provider, so useRoomHealth can subscribe to it. In state (not a ref) because the
   *  subscription must be re-keyed when the room changes; it is created once per room. */
  provider: HocuspocusProvider
}

/** How many § locks the lockbar lists before collapsing the rest into "+N more". */
const LOCKBAR_MAX = 3
/** A rejected keystroke repeats as fast as the member types — toast at most this often. */
const REJECT_TOAST_MS = 4000
/** The outline panel id, so the toggle's aria-controls resolves. */
const OUTLINE_ID = 'sec-outline-panel'

/** How long a room may take to open before the member is told, rather than shown a blank page. */
const OPENING_GRACE_MS = 1500

/** How hard to chase a refused handshake before leaving it to the member's next real trigger.
 *  Five attempts over ~30s of backoff covers a token that expired under a sleeping machine; past
 *  that the credential is not the problem and retrying is just noise on the server. */
const AUTH_RETRY_LIMIT = 5
const AUTH_RETRY_BASE_MS = 1000
const AUTH_RETRY_MAX_MS = 30_000

/** Where a § lock bites, as it is both shown and read out loud: "§3.2.4 Technologies".
 *  Only meaningful when `section` is set — a record without one holds the whole part. */
function lockLabel(rev: Revision): string {
  return `§${rev.section}${rev.sectionTitle ? ` ${rev.sectionTitle}` : ''}`
}

/**
 * The ONE display name of a lock holder, agent attribution included.
 *
 * Computed once and fed to both the lock bar and `editorLocks` (whose `actorName` becomes the
 * shading chip's `data-lock-actor`), so the bar and the chip can never disagree about who is
 * holding a §. The naming itself lives in lib/actor-name.ts, shared with the history table and its
 * toasts, so a server-written agent edit is labelled identically wherever it surfaces. A nameless
 * record is a legacy write — a §-scoped one falls back to the raw userId, a whole-part one to
 * "Niobe", exactly as the bar has always read.
 */
function holderName(rev: Revision, agentPattern: string): string {
  return actorDisplayName(rev, rev.section ? rev.actor : 'Niobe', agentPattern)
}

/**
 * Which "this one is yours" note follows the holder name on a lock held under the member's own
 * identity. An agent-held lock already READS as "Alex's agent", so appending "your console agent"
 * said the same thing twice — there it only has to mark ownership ("yours"). A human-held lock
 * under the member's id keeps the wording that explains what is going on.
 */
function ownNote(rev: Revision): 'sec_lock_yours' | 'sec_lock_mine' {
  return rev.actorKind === 'agent' ? 'sec_lock_yours' : 'sec_lock_mine'
}

/**
 * Live collaborative document inside the mockup's "paper" canvas. The CRDT is a
 * Markdown Y.Text (the server derives markdown from it), edited through the single
 * "Rich" WYSIWYG (Word-like) view. AI edits apply directly (no accept/reject) and,
 * alongside the user's own manual edits, are recorded in the room's 'history' Y.Array
 * — consulted read-only in the HistoryModal as an inline diff.
 */
export function Canvas({ room, kind = 'markdown', artefactType = 'prose', wsUrl, user, readOnly = false, partLock, viewer = false, partTitle, artefactTitle, onOpenPart, beta = false, admin = false, focusMode = false, onToggleFocus, chatReopen }: CanvasProps) {
  const { t } = useLang()
  // Prose-only controls (§ outline, formatting, word counts, text history capture, restore) are
  // hidden on every structured part — a grid, and a slides part.
  const isProse = kind === 'markdown'
  // The header's human title. Before the outline has loaded, the room's own segments stand in —
  // never the raw "bid/…" key.
  const roomSegs = room?.split('/') ?? []
  const headerTitle = `${artefactTitle ?? roomSegs[1] ?? ''} / ${partTitle ?? roomSegs[2] ?? ''}`
  // A slides part brings its own editor chrome (sorter, canvas that fits its column, inspector):
  // the paper's zoom has nothing to act on there, and its Find button sits in the deck's own bar.
  const isSlides = kind === 'slides'
  const [conn, setConn] = useState<Conn | null>(null)
  // Below the backend's version floor (lib/client-floor.ts): no room may stay open on this bundle's
  // provider. A dependency of the room effect below, so turning outdated closes the open room
  // (its cleanup destroys the provider) and the effect then declines to open another.
  const outdated = useClientOutdated() !== null
  // Room epochs (AB#124, lib/room-epoch.ts): bumped when the server refuses this tab's Y.Doc with
  // `room_restored`, which makes the room effect below drop it and reopen the part on a new one.
  const [roomGeneration, setRoomGeneration] = useState(0)
  // The part open now was reopened that way: say so until another part is opened.
  const [restoredNotice, setRestoredNotice] = useState(false)
  useEffect(() => setRestoredNotice(false), [room])
  const [participants, setParticipants] = useState<Participant[]>([])
  const [revisions, setRevisions] = useState<Revision[]>([])
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  // The room's restore points (GET /v1/versions, paged — AB#241), read while the History modal is
  // open and re-read as the log grows: the store hook tags a new entry's restore point a moment
  // after the entry itself lands (re-read until it is there). Read back past the log's oldest entry,
  // so every entry's is known. Keyed on the newest entry: at the 50-entry cap the length stays put.
  const newestEntryId = history[history.length - 1]?.id ?? null
  const roomVersions = useRoomVersions(room, historyOpen, history[0]?.createdAt ?? null, newestEntryId, newestEntryId)
  // The history entries that have a server restore point (→ historyId). `null` until read (or when
  // they could not be).
  const restorePoints = useMemo(
    () => (roomVersions.versions ? new Set(roomVersions.versions.flatMap((v) => (v.historyId ? [v.historyId] : []))) : null),
    [roomVersions.versions],
  )
  const [outlineOpen, setOutlineOpen] = useState(false)
  // Ctrl+F opens 'find' (no replacement field), Ctrl+H opens 'replace'; a panel already open just
  // switches mode in place (see the shortcut effect below) rather than closing and reopening.
  const [findOpen, setFindOpen] = useState(false)
  const [findMode, setFindMode] = useState<'find' | 'replace'>('find')
  // What a shortcut press hands the panel: the selected text, and a counter so a press while it is
  // already open still re-seeds and re-focuses it.
  const [findSeed, setFindSeed] = useState<{ text: string; n: number }>({ text: '', n: 0 })
  const [toasts, setToasts] = useState<Toast[]>([])
  // Locks are shaded in the editor; shading alone is not perceivable to everyone, so arrival,
  // release and copy-confirmation are also spoken through a polite live region (WCAG 1.4.1).
  const [announcement, setAnnouncement] = useState('')
  const [partStats, setPartStats] = useState<TextStats>({ words: 0, lines: 0 })
  const [docRooms, setDocRooms] = useState<Record<string, TextStats>>({})
  // Where this part's § numbering starts inside its deliverable — see the fetch below.
  // null = "don't know yet" (fetching for the room just opened), never a guessed 0 — see the
  // reset below and SectionNumbers.ts for why that distinction is what kills the numbering flash.
  const [sectionOffset, setSectionOffset] = useState<number | null>(null)
  const [editor, setEditor] = useState<Editor | null>(null)
  /** The open deck's find surface (AB#326), handed up by SlidesEditor as RichEditor hands up `editor`. */
  const [deckFind, setDeckFind] = useState<DeckFindApi | null>(null)
  // Zoom applies to the document content only (the "paper"), not the rest of the UI. Persisted.
  const [zoom, setZoom] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('niobe.zoom'))
      return v >= 0.5 && v <= 2 ? v : 1
    } catch {
      return 1
    }
  })
  const setZoomClamped = (z: number): void => {
    const c = Math.min(2, Math.max(0.5, Math.round(z * 10) / 10))
    setZoom(c)
    try {
      localStorage.setItem('niobe.zoom', String(c))
    } catch {
      /* storage unavailable */
    }
  }


  useEffect(() => {
    if (!room || outdated) {
      setConn(null)
      return
    }
    const ydoc = new Y.Doc()

    // Recovery from a REFUSED handshake. @hocuspocus/provider treats one refusal as final — it
    // detaches the provider from its socket layer (`providerMap.delete`), and a 4401 close also
    // sets `shouldConnect = false` — so a room refused once is dead for the life of the page unless
    // something puts it back. `reauthenticateRoom` is that something (and it needs a NEW socket,
    // not just a re-attach — see its doc comment).
    //
    // Only 'Unauthorized' is worth retrying: it means the credential, and a fresh one may work.
    // 'Forbidden: not a member of this bid' is a real permission decision (the backend spells the
    // reason out for exactly this purpose, see backend/inbound/ws/hocuspocus.ts#refusal) and
    // retrying it would hammer the server over a question already answered.
    //
    // The backoff below gives up after AUTH_RETRY_LIMIT, but a refused room is not left for dead:
    // it retries WHENEVER a new token turns up, from anywhere (`onTokenRenewed`). After a sleep the
    // renewal that works usually comes later than any backoff would wait: once the network is back
    // (lib/connectivity.ts), or once the member signs in again from the session-expired banner.
    let authAttempts = 0
    // Every pending backoff, not just the latest: a burst of refusals schedules several, and any
    // left running would fire after the room is closed.
    const authTimers = new Set<ReturnType<typeof setTimeout>>()
    // Refused and not yet re-attempted with a new credential.
    let awaitingToken = false
    const retryRefused = (): void => {
      if (!awaitingToken) return
      awaitingToken = false
      reauthenticateRoom(provider)
    }
    const onRefused = ({ reason }: { reason: string }): void => {
      if (isRoomRestored(reason)) {
        onRestored()
        return
      }
      // This bundle is below the floor: never retried — the room effect closes on the verdict.
      if (isOutdatedRefusal(reason)) {
        markClientOutdated('spa')
        return
      }
      if (!reason.startsWith('Unauthorized')) return
      awaitingToken = true
      if (authAttempts >= AUTH_RETRY_LIMIT) return
      const backoff = Math.min(AUTH_RETRY_BASE_MS * 2 ** authAttempts, AUTH_RETRY_MAX_MS)
      authAttempts += 1
      const refused = getToken()
      const authTimer = setTimeout(() => {
        authTimers.delete(authTimer)
        void renewToken().then((next) => {
          // Unchanged means the renewal failed and fell back to what we already hold. Reconnecting
          // with it would be refused again — `onTokenRenewed` below retries once a renewal works.
          if (!next || next === refused) return
          retryRefused()
        })
      }, backoff)
      authTimers.add(authTimer)
    }
    const stopRenewed = onTokenRenewed(retryRefused)

    // The room's state was replaced server-side (a restore) since this Y.Doc synced it: drop the
    // provider AND the Y.Doc (this effect's cleanup) and reopen the part fresh — never resync the
    // old content into the restored room. Once per Y.Doc: the refusal and the close may both fire.
    let restored = false
    const onRestored = (): void => {
      if (restored) return
      restored = true
      setRestoredNotice(true)
      setRoomGeneration((g) => g + 1)
    }
    // The epoch the server announces for this Y.Doc, presented back on every reconnect. The
    // provider keeps this very object and rebuilds its URL from it (lib/room-epoch.ts).
    const parameters: Record<string, unknown> = roomParameters()
    const onStateless = ({ payload }: { payload: string }): void => noteRoomEpoch(parameters, payload)

    // Status is NOT mirrored into local state here: useRoomHealth owns it, because "connected" on
    // its own is not something the UI may show as live (see lib/room-connection.ts).
    const provider = new HocuspocusProvider({
      url: wsUrl,
      name: room,
      // A FUNCTION, not a string. The provider awaits it on every `onOpen`, so each reconnect
      // presents a live credential. Passing the token captured at page load is what made a slept
      // or long-idle app show blank artefacts until it was quit from the tray — see
      // lib/session-token.ts.
      token: () => freshToken(),
      document: ydoc,
      // This bundle's version, which the backend's SPA_MIN_VERSION floor is enforced against, and
      // (once announced) the room epoch of this Y.Doc.
      parameters,
      onAuthenticationFailed: onRefused,
      // The same verdicts when they arrive as a CLOSE reason rather than a refusal frame (an
      // eviction of the live room closes it with `room_restored`).
      onClose: ({ event }) => {
        if (isOutdatedRefusal(event?.reason)) markClientOutdated('spa')
        if (isRoomRestored(event?.reason)) onRestored()
      },
      onAuthenticated: () => {
        authAttempts = 0
        awaitingToken = false
      },
    })
    provider.on('stateless', onStateless)
    const ytext = ydoc.getText('content')
    const awareness = provider.awareness
    awareness?.setLocalStateField('user', { name: user.name, color: user.color })

    const refreshParticipants = (): void => {
      if (!awareness) return
      const list: Participant[] = []
      awareness.getStates().forEach((state, clientId) => {
        const u = (state as { user?: { name: string; color: string } }).user
        if (u) list.push({ clientId, name: u.name, color: u.color })
      })
      setParticipants(list)
    }
    awareness?.on('change', refreshParticipants)
    refreshParticipants()

    const revs = ydoc.getMap('revisions')
    const refreshRevisions = (): void => setRevisions([...revs.values()] as Revision[])
    revs.observe(refreshRevisions)
    refreshRevisions()

    // Toast only modifications that happen AFTER this page opened. Entries that load from
    // persistence/sync carry a past timestamp, so they never notify (no re-toasting the
    // historical edits on every page load). We also skip the user's own manual edits.
    const hist = ydoc.getArray('history')
    const openedAt = Date.now()
    const seen = new Set<string>()
    const refreshHistory = (): void => {
      const arr = hist.toArray() as unknown as HistoryEntry[]
      setHistory(arr)
      const fresh = arr.filter((e) => !seen.has(e.id))
      fresh.forEach((e) => seen.add(e.id))
      const notify = fresh.filter(
        (e) =>
          new Date(e.createdAt).getTime() > openedAt &&
          !(e.kind === 'human' && e.actor === user.name),
      )
      if (notify.length) {
        setToasts((prev) => [
          ...prev,
          ...notify.map((e) => ({
            id: e.id,
            actor: e.actor,
            kind: e.kind,
            // Display-only attribution, so a console-agent edit is announced the way the lock bar
            // announced it ("Sofia's agent") rather than as a bare userId.
            ...(e.actorName ? { actorName: e.actorName } : {}),
            ...(e.actorKind ? { actorKind: e.actorKind } : {}),
            ...(e.prompt ? { prompt: e.prompt } : {}),
            ...(e.before ? { before: e.before } : {}),
            ...(e.after ? { after: e.after } : {}),
            ...(e.contextBefore ? { contextBefore: e.contextBefore } : {}),
            ...(e.contextAfter ? { contextAfter: e.contextAfter } : {}),
          })),
        ])
      }
    }
    hist.observe(refreshHistory)
    refreshHistory()

    // Live word/line stats of the open part.
    const updateStats = (): void => setPartStats(textStats(ytext.toString()))
    ytext.observe(updateStats)
    updateStats()

    setConn({ room, ydoc, ytext, awareness, provider })

    return () => {
      awareness?.off('change', refreshParticipants)
      revs.unobserve(refreshRevisions)
      hist.unobserve(refreshHistory)
      ytext.unobserve(updateStats)
      setHistory([])
      setToasts([])
      // Presence belongs to the room that is closing: leaving it behind would let
      // "Live · N editing" report the PREVIOUS part's headcount for the next room's first frames.
      setParticipants([])
      setConn(null)
      authTimers.forEach(clearTimeout)
      stopRenewed()
      provider.off('stateless', onStateless)
      provider.destroy()
      ydoc.destroy()
    }
  }, [room, wsUrl, user.name, user.color, outdated, roomGeneration])

  // A revision IS the § lock (it lives in Y.Map('revisions')), so it holds a section only while
  // it is still `working` AND unexpired. `lockNow` is bumped by the timer below so a lapsed lock
  // clears itself without waiting for another CRDT update.
  const [lockNow, setLockNow] = useState(() => Date.now())
  const workingLocks = useMemo(
    () => revisions.filter((r) => r.status === 'working' && !lockExpired(r, lockNow)),
    [revisions, lockNow],
  )
  useEffect(() => {
    const next = revisions
      .filter((r) => r.status === 'working')
      .map(lockLapsesAt)
      .filter((at): at is number => at !== null && at > lockNow)
      .sort((a, b) => a - b)[0]
    if (next === undefined) return
    const timer = setTimeout(() => setLockNow(Date.now()), next - lockNow + 200)
    return () => clearTimeout(timer)
  }, [revisions, lockNow])

  /**
   * Who this tab is, for the lock carve-out — a per-CONNECTION id, never the member's identity.
   *
   * The previous rule ("carve the lock out when its actorName equals my display name") let the
   * member's own console agent stop blocking their browser, which is precisely backwards: two
   * workers on one Bearer token are still two workers, exactly as backend/domain/locks.ts#blocks
   * reasons when it refuses to exempt an actor wholesale. Only a lock THIS connection opened may
   * be exempt — and the web opens none, since it never POSTs /v1/revisions. So nothing is carved
   * out today, and the id below is what keeps that true by construction rather than by accident.
   */
  const connectionId = useMemo(() => `web:${conn?.awareness?.clientID ?? 'local'}`, [conn])

  // The member's own userId, so a lock their console agent holds can be LABELLED as theirs.
  // Deliberately not part of the carve-out — see above.
  const myUserId = useMyUserId()

  // Lock notices (AB#301): another member's agent was refused by — or wrote over — the lock THIS
  // member holds (their §, cell or slide selection). Stateless room messages, shown to the holder
  // only. Release bumps `releaseSignal`, which the open editor reads as "drop my lock now".
  const lockNotices = useLockNotices(conn?.provider ?? null, myUserId)
  const [releaseSignal, setReleaseSignal] = useState(0)
  const clearLockNotices = lockNotices.clear
  const releaseLock = useCallback((): void => {
    setReleaseSignal((n) => n + 1)
    clearLockNotices()
  }, [clearLockNotices])

  // The localised "{name}'s agent" form, resolved once per language change and threaded into the
  // single holder-name helper the bar and the chip share.
  const agentPattern = t('sec_lock_agent')

  // What the editor extension enforces. `actor` is the real holder's userId, handed through
  // untouched; it can never equal `connectionId`, so every live lock restrains this tab.
  // `actorName` is the shading chip's text (`data-lock-actor`) — same helper as the bar, so the
  // two can never attribute a lock differently.
  const editorLocks = useMemo<SectionLock[]>(
    () =>
      workingLocks.map((r) => ({
        section: r.section ?? '',
        actorName: holderName(r, agentPattern),
        actor: r.actor,
      })),
    [workingLocks, agentPattern],
  )

  // Agent-held claims are reported in the HEADER (replacing the title), never in the in-editor
  // lock bar: the body must not shift while an agent works. The header keeps the sentence for
  // AGENT_ACTIVITY_LINGER_MS after the release, then slides it into History (lib/lock-view.ts).
  const agentLocks = useMemo(() => workingLocks.filter(isAgentLock), [workingLocks])
  const humanLocks = useMemo(() => workingLocks.filter((r) => !isAgentLock(r)), [workingLocks])
  const { activity, landed } = useAgentActivity(agentLocks, room)

  // The lock bar lists the live HUMAN locks plus the ones that just left. The EDITOR keeps the
  // live set only — a § shaded as locked when it is not would be a lie the member could act on.
  const barLocks = useLockAfterglow(humanLocks)

  // Honest transport health. `healthy` (live socket, connected AND synced) is the ONLY licence to
  // show a green dot; `stale` means the member is looking at content we cannot vouch for. See
  // lib/room-connection.ts for why the provider's own status is not enough.
  const health = useRoomHealth(conn?.provider ?? null)
  // Four honest states, never a claim the transport cannot back: live (with the headcount, in the
  // member's language), connected-but-still-handshaking, a FIRST connect, and a genuine reconnect
  // after a room that had been live. `everSynced` is what separates the last two — telling someone
  // their connection was lost when it never existed is a false alarm.
  const editing = participants.length || 1
  const connLabel = !room
    ? '—'
    : health.healthy
      ? editing === 1
        ? t('live_1')
        : t('live_n').replace('{n}', () => String(editing))
      : health.status === 'connected'
        ? t('conn_syncing')
        : health.everSynced
          ? t('conn_reconnecting')
          : t('conn_connecting')

  // A room that has not synced YET is just the first frames of opening a part — showing anything
  // about it there would flicker on every open, which is the bug d996a2b fixed on the bid overview.
  // A room that has not synced after the grace period is a different thing entirely, and the member
  // has to be told rather than shown a blank page. A refusal skips the grace: it is already an
  // answer, not a wait.
  const [openingLate, setOpeningLate] = useState(false)
  useEffect(() => {
    setOpeningLate(false)
    if (!room || health.everSynced) return
    const id = setTimeout(() => setOpeningLate(true), OPENING_GRACE_MS)
    return () => clearTimeout(id)
  }, [room, health.everSynced])
  // `everSynced` only ever ratchets up, so this can never unmount an editor that once worked.
  const unopened = !!room && !health.everSynced && (openingLate || health.authFailed)

  // A slides part: fetch the deck's templates NOW — its directory, then its default template's view
  // (AB#445) — in parallel with the editor's chunk and the room's sync, instead of after the editor
  // mounted (AB#283). Optional client: a test may render bare.
  const queryClient = useContext(QueryClientContext)
  const slidesRoom = isSlides && room ? room : null
  const slidesBid = slidesRoom ? (slidesRoom.split('/')[0] ?? null) : null
  useEffect(() => {
    if (queryClient && slidesBid && slidesRoom) void prefetchDeckTemplates(queryClient, slidesBid, slidesRoom)
  }, [queryClient, slidesBid, slidesRoom])

  // Shared flag: true while the "improve with AI" flow applies its replacement, so the
  // human-edit capture ignores those writes (the AI flow records its own history entry).
  const aiApplying = useRef(false)

  // Record this user's own manual editing bursts into the room history (CRDT-only). MARKDOWN only:
  // it diffs a Y.Text, which a grid room keeps empty on purpose. GridEditor writes its own entries,
  // into the same Y.Array('history'), from the cell it actually changed.
  useHistoryCapture(conn?.ydoc ?? null, isProse ? (conn?.ytext ?? null) : null, user.name, aiApplying)

  /** Publish to the live region. Re-announcing the SAME text needs a different string, or the
   *  DOM never changes and the screen reader stays silent — hence the trailing-space flip. */
  const announce = useCallback((text: string): void => {
    setAnnouncement((prev) => (prev === text ? `${text} ` : text))
  }, [])

  const noticeSeq = useRef(0)
  const notify = useCallback(
    (message: string): void => {
      setToasts((prev) => [
        ...prev,
        { id: `notice-${++noticeSeq.current}`, actor: user.name, kind: 'human', message },
      ])
    },
    [user.name],
  )

  /** A slide claim, named by the slide's place in the deck ("Slide 2") — read off the room's own
   *  slideOrder rather than through the slides model, which lives in the editor's lazy chunk. */
  const slideLabel = useCallback(
    (slide: string): string => {
      const at = conn ? (conn.ydoc.getArray<string>('slideOrder').toArray().indexOf(slide)) : -1
      return at < 0 ? t('sl_a_slide') : t('sl_slide_n').replace('{n}', String(at + 1))
    },
    [conn, t],
  )
  /** A slide in the History modal: by its place in the deck today, or gone (the slides editor's
   *  former History tab named it the same way). */
  const historySlideName = useCallback(
    (slide: string): string => {
      const at = conn ? conn.ydoc.getArray<string>('slideOrder').toArray().indexOf(slide) : -1
      return at < 0 ? t('sl_slide_gone') : t('sl_slide_n').replace('{n}', String(at + 1))
    },
    [conn, t],
  )

  /** A claim's sentence, the same shape in the lock bar (human §/slide locks) and the header
   *  (agent claims): "§3.2 Title — Name (yours) · prompt", or for the whole part
   *  "Name (yours) is editing this part… prompt". An agent claim draws its holder as the roster's
   *  robot chip (the member's initials) instead of the name — the full "Name's agent (yours)" stays
   *  the chip's accessible name and tooltip. `lockSentenceText` is the plain form, for a tooltip. */
  const holderLabel = (rev: Revision): string =>
    `${holderName(rev, agentPattern)}${rev.actor === myUserId ? ` (${t(ownNote(rev))})` : ''}`
  const lockSentence = (rev: Revision): ReactElement => {
    const who = isAgentLock(rev) ? (
      <AgentChip name={rev.actorName ?? (rev.section ? rev.actor : 'Niobe')} label={holderLabel(rev)} />
    ) : (
      <>
        <b style={{ fontWeight: 700 }}>{holderName(rev, agentPattern)}</b>
        {rev.actor === myUserId && <> ({t(ownNote(rev))})</>}
      </>
    )
    return rev.section || rev.slide ? (
      <>
        <span className="lock-sec">{rev.slide ? slideLabel(rev.slide) : lockLabel(rev)}</span>
        {' — '}
        {who}
        {rev.prompt && <> · <strong style={{ color: 'var(--info-ink)' }}>{rev.prompt}</strong></>}
      </>
    ) : (
      /* Whole-part claim: keep the wording the member already knows. */
      <>
        {who}
        {' '}{t('editing')} <strong style={{ color: 'var(--info-ink)' }}>{rev.prompt}</strong>
      </>
    )
  }
  const lockSentenceText = (rev: Revision): string => {
    const who = holderLabel(rev)
    return rev.section || rev.slide
      ? `${rev.slide ? slideLabel(rev.slide) : lockLabel(rev)} — ${who}${rev.prompt ? ` · ${rev.prompt}` : ''}`
      : `${who} ${t('editing')} ${rev.prompt}`
  }
  const moreText = activity && activity.more > 0 ? ` +${activity.more} ${t('sec_more')}` : ''
  const activityText = activity ? `${lockSentenceText(activity.rev)}${moreText}` : ''
  const activitySentence = activity ? (
    <>
      {lockSentence(activity.rev)}
      {moreText && <span className="lock-more">{moreText}</span>}
    </>
  ) : null

  // Aim the leave transition at the History button: the sentence slides right by the distance
  // between the two, measured as it starts leaving (layout effect, so before the first frame).
  const activityRef = useRef<HTMLSpanElement>(null)
  const histBtnRef = useRef<HTMLButtonElement>(null)
  const leaving = activity?.phase === 'leaving'
  useLayoutEffect(() => {
    const el = activityRef.current
    const target = histBtnRef.current
    if (!leaving || !el || !target) return
    const from = el.getBoundingClientRect()
    const to = target.getBoundingClientRect()
    el.style.setProperty('--fly-x', `${Math.round(to.left + to.width / 2 - (from.left + from.width / 2))}px`)
  }, [leaving])

  // Speak lock arrival/release. Seeded per room, so opening a part someone already holds does
  // not shout the pre-existing locks at the member. Keyed on `lockKey` (who holds which §) rather
  // than on the revision id: the console agent's heartbeat re-claims the same lock under a NEW id
  // every ~100s, which id-keying heard as a fresh arrival to announce all over again.
  const heldRef = useRef<{ room: string | null; keys: string[] } | null>(null)
  useEffect(() => {
    const keys = workingLocks.map(lockKey)
    const prev = heldRef.current
    heldRef.current = { room, keys }
    if (!prev || prev.room !== room) return
    const arrived = workingLocks.find((r) => !prev.keys.includes(lockKey(r)))
    if (arrived) {
      const where = arrived.section ? lockLabel(arrived) : arrived.slide ? slideLabel(arrived.slide) : t('sec_whole_part')
      // Same holder name the bar and the chip show — spoken attribution must not drift from the
      // visible one.
      announce(`${where} ${t('sec_lock_taken')} ${holderName(arrived, agentPattern)}`)
      return
    }
    if (keys.length < prev.keys.length) announce(t('sec_lock_freed'))
  }, [workingLocks, room, announce, agentPattern, t, slideLabel])

  // Speak the connection going stale and coming back, through the SAME polite region as the
  // locks — the visible banner below deliberately does not announce itself (two live regions
  // would say everything twice). Seeded per room: switching parts is not a transition.
  const staleRef = useRef<{ room: string | null; stale: boolean }>({ room: null, stale: false })
  useEffect(() => {
    const prev = staleRef.current
    staleRef.current = { room, stale: health.stale }
    if (prev.room !== room || prev.stale === health.stale) return
    announce(health.stale ? t('conn_stale') : t('conn_restored'))
  }, [health.stale, room, announce, t])

  /** The MVP-1 workflow is "read the number, hand it to your console agent", so the copied
   *  string is exactly what the agent's § tools resolve against this room. */
  const copySectionRef = useCallback(
    (number: string, title: string): void => {
      const reference = `${room ?? ''} §${number}${title ? ` "${title}"` : ''}`.trim()
      void navigator.clipboard?.writeText(reference).catch(() => undefined)
      notify(`${t('sec_copied')} ${reference}`)
      announce(`${t('sec_copied')} ${reference}`)
    },
    [room, notify, announce, t],
  )

  /** The header title's click: a link a colleague can open — on the PUBLIC web origin, never the
   *  desktop's localhost (lib/router.ts#partShareUrl). The toast alone speaks it: the toast stack is
   *  itself a polite live region, so an announce() on top would say it twice. */
  const copyPartLink = useCallback((): void => {
    if (!room) return
    void navigator.clipboard?.writeText(partShareUrl(room)).catch(() => undefined)
    notify(t('part_link_copied'))
  }, [room, notify, t])

  // A rejected keystroke repeats as fast as the member types, so throttle the toast. Honest
  // framing: this filter is UX — the § is actually isolated by the server rejecting the write.
  const rejectedAt = useRef(0)
  const onLockRejected = useCallback((): void => {
    const now = Date.now()
    if (now - rejectedAt.current < REJECT_TOAST_MS) return
    rejectedAt.current = now
    notify(t('sec_lock_deny'))
  }, [notify, t])

  // Document (deliverable) stats: fetch the bid's per-room counts when the room changes.
  useEffect(() => {
    if (!room) {
      setDocRooms({})
      return
    }
    const bid = room.split('/')[0]
    if (!bid) return
    let cancelled = false
    api.stats(bid).then((d) => !cancelled && setDocRooms(d.rooms)).catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [room])

  // § numbering runs continuously across the parts of a deliverable, so this part starts where
  // the ones before it stopped. That offset depends on SIBLING rooms, which this client never
  // connects to — so unlike the locks (which ride the room's own Y.Map) it comes over HTTP, and
  // is re-read when the part changes. It can lag a sibling edit until then; the § a lock holds
  // is re-anchored by title server-side when its number drifts, so a stale offset misplaces a
  // label, never a write. Reset to null (not 0) first: numbering part 2 with part 3's offset —
  // or with a guessed 0 — for a frame would flash wrong numbers on screen either way; null makes
  // SectionNumbers/SectionOutline withhold the numbers instead of showing one that's about to
  // change (AB#30 follow-up).
  useEffect(() => {
    setSectionOffset(null)
    if (!room) return
    let cancelled = false
    api
      .sections(room)
      .then((d) => !cancelled && setSectionOffset(d.offset))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [room])

  // The document = all parts of the open part's deliverable; the open part is counted
  // live (more current than the stored snapshot), the siblings from the fetched stats.
  const docTotal = useMemo(() => {
    if (!room) return null
    const prefix = room.split('/').slice(0, 2).join('/') + '/'
    let words = partStats.words
    let lines = partStats.lines
    for (const [r, s] of Object.entries(docRooms)) {
      if (!r.startsWith(prefix) || r === room) continue
      words += s.words
      lines += s.lines
    }
    return { words, lines }
  }, [docRooms, partStats, room])

  /** The entries prose can rebuild from its own log (reconstructAt), while the modal is open. */
  const rebuildable = useMemo(
    () => (historyOpen && isProse && conn ? reconstructableIds(history, conn.ytext.toString()) : new Set<string>()),
    [historyOpen, isProse, conn, history],
  )
  /** An entry is restorable when it has a restore point — or, on prose, when its log rebuilds it. */
  const restorable = useCallback(
    (entry: HistoryEntry): boolean => Boolean(restorePoints?.has(entry.id)) || rebuildable.has(entry.id),
    [restorePoints, rebuildable],
  )

  /** Say why a server restore was refused, in the member's words — a 409 is reported, not swallowed. */
  function reportRestoreFailure(e: unknown, fallback: string): void {
    if (isApiError(e) && e.code === 'room_busy') reportError(t('hist_room_busy'))
    else if (isApiError(e) && e.code === 'no_restore_point') reportError(t('hist_no_restore_point'))
    else if (isApiError(e) && e.serverMessage) reportError(fallback, e.serverMessage)
    else if (!isApiError(e) || e.status !== 401) reportError(fallback)
  }

  /**
   * Restore the part IN PLACE through the server restore point tagged with `entry` (POST
   * /v1/restore { historyId }) — the path for a grid or a deck, and prose's fallback when its log
   * diverged. The server snapshots "before restore" first (so it is reversible) and logs the restore
   * as a history entry; the result reaches this editor through the CRDT like any other write.
   */
  function restoreOnServer(entry: HistoryEntry): void {
    if (!room) return
    if (isAgentBusy()) {
      reportError(t('busy_generic'))
      return
    }
    api
      .restoreHistoryEntry(room, entry.id)
      .then(() => setHistoryOpen(false))
      .catch((e: unknown) => reportRestoreFailure(e, t('restore_failed')))
  }

  /** In-place Restore: prose keeps its exact client path; everything else goes through the server. */
  function restoreInPlace(entry: HistoryEntry): void {
    if (isProse) restoreTo(entry)
    else restoreOnServer(entry)
  }

  /**
   * "Restore as part": a NEW read-only part with the state right after `entry`, named after this
   * part and the entry's instant (local time), right after it — then opened. The source is the
   * entry's restore point when it has one; prose without one sends the text its log rebuilds.
   */
  function restoreAsPart(entry: HistoryEntry): void {
    if (!room || !conn) return
    let source: { historyId: string } | { markdown: string; at: string }
    if (restorePoints?.has(entry.id)) source = { historyId: entry.id }
    else {
      const markdown = isProse ? reconstructAt(history, conn.ytext.toString(), entry.id) : null
      if (markdown === null) {
        reportError(t('hist_no_restore_point'))
        return
      }
      source = { markdown, at: entry.createdAt }
    }
    const name = restoreCopyName(partTitle ?? room.split('/').pop() ?? room, entry.createdAt)
    api
      .restoreCopy(room, source, name)
      .then((res) => {
        setHistoryOpen(false)
        onOpenPart?.(res.room)
      })
      .catch((e: unknown) => reportRestoreFailure(e, t('hist_restore_copy_failed')))
  }

  /** Restore the part IN PLACE to an older version — one whose entry has left the log (AB#241). */
  function restoreVersionInPlace(v: VersionMeta): void {
    if (!room) return
    if (isAgentBusy()) {
      reportError(t('busy_generic'))
      return
    }
    api
      .restore(room, v.version)
      .then(() => setHistoryOpen(false))
      .catch((e: unknown) => reportRestoreFailure(e, t('restore_failed')))
  }

  /** "Restore as part" from an older version: the same named, read-only copy, opened. */
  function restoreVersionAsPart(v: VersionMeta): void {
    if (!room) return
    const name = restoreCopyName(partTitle ?? room.split('/').pop() ?? room, v.createdAt)
    api
      .restoreCopy(room, { version: v.version }, name)
      .then((res) => {
        setHistoryOpen(false)
        onOpenPart?.(res.room)
      })
      .catch((e: unknown) => reportRestoreFailure(e, t('hist_restore_copy_failed')))
  }

  /** What an older version changed (GET /v1/versions/:v/diff). Stable: each row reads it once. */
  const loadVersionDiff = useCallback((version: number) => api.versionDiff(room ?? '', version), [room])

  /** Flatten's dry run (AB#243). Stable: the dialog re-reads it as the kept version changes. */
  const previewFlatten = useCallback((keep: number) => api.flattenVersions(room ?? '', keep, true), [room])

  /** Flatten the history down to `keep`, then read the list again. The dialog words a refusal. */
  function flatten(keep: number): Promise<unknown> {
    if (!room) return Promise.reject(new Error('no room'))
    return api.flattenVersions(room, keep, false).then(() => roomVersions.reload())
  }

  /** Tag / comment a version; the server's answer replaces the row, a refusal is said and rethrown. */
  function updateVersion(version: number, patch: VersionPatch): Promise<unknown> {
    if (!room) return Promise.reject(new Error('no room'))
    return api
      .updateVersion(room, version, patch)
      .then((res) => roomVersions.replace(res.version))
      .catch((e: unknown) => {
        if (isApiError(e) && e.serverMessage) reportError(t('hist_version_failed'), e.serverMessage)
        else if (!isApiError(e) || e.status !== 401) reportError(t('hist_version_failed'))
        throw e
      })
  }

  /**
   * Restore the document to the content it had right after a history entry, by reverse-applying
   * the newer entries. Applied through the editor under the AI-apply guard (so the human-edit
   * capture ignores it), then logged as its own history entry. Refuses if the history diverged.
   */
  function restoreTo(entry: HistoryEntry): void {
    if (!conn) return
    if (isAgentBusy()) {
      // An agent is editing — restoring now would race the in-flight CRDT writes.
      reportError(t('busy_generic'))
      return
    }
    const before = conn.ytext.toString()
    const target = reconstructAt(history, before, entry.id)
    if (target === null) {
      // The log diverged (concurrent edits): the entry's server restore point, when it has one.
      // (Checked before the editor: the server path does not go through it.)
      if (restorePoints?.has(entry.id)) restoreOnServer(entry)
      else reportError(t('restore_failed'))
      return
    }
    // A destroyed editor (tiptap tears instances down on its own timer) cannot take commands.
    if (!editor || editor.isDestroyed) return
    if (target === before) {
      setHistoryOpen(false)
      return
    }
    aiApplying.current = true
    try {
      editor.commands.setContent(target, { emitUpdate: true })
    } finally {
      aiApplying.current = false
    }
    const after = conn.ytext.toString()
    const logged = makeHistoryEntry({
      actor: user.name,
      kind: 'human',
      before,
      after,
      prompt: `${t('restore_prompt')} ${new Date(entry.createdAt).toLocaleString()}`,
    })
    if (logged) appendHistory(conn.ydoc, logged)
    setHistoryOpen(false)
  }

  // The panel belongs to the room that opened it: a stale editor/ydoc/ytext underneath it (the
  // part just switched) is worse than making the member reopen it, so it closes rather than
  // follow the room — unless Find itself opened this room to go to a match (AB#366): then it
  // stays open and re-binds to the new room's editor (lib/findStore.ts).
  useEffect(() => {
    const find = useFindStore.getState()
    if (room && find.pending?.room === room) return
    find.clearPending()
    setFindOpen(false)
  }, [room])

  // The § outline sticks under the toolbar and is sized against the editor's visible height, so
  // both are measured live: the scrollport's height feeds the outline's 25% cap, and the toolbar's
  // (it wraps on narrow widths) is published as --fmtbar-h for the outline's sticky `top` and the
  // headings' scroll-margin-top.
  const wrapRef = useRef<HTMLDivElement>(null)
  const fmtbarRef = useRef<HTMLDivElement>(null)
  const [editorHeight, setEditorHeight] = useState(0)
  useEffect(() => {
    const wrap = wrapRef.current
    const bar = fmtbarRef.current
    if (!wrap || !bar || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      setEditorHeight(wrap.clientHeight)
      wrap.style.setProperty('--fmtbar-h', `${bar.offsetHeight}px`)
    })
    observer.observe(wrap)
    observer.observe(bar)
    return () => observer.disconnect()
    // A slides part renders no toolbar: observe again once a prose or grid part brings it back.
  }, [isSlides])

  function openFind(mode: 'find' | 'replace', text = ''): void {
    setFindMode(mode)
    setFindSeed((prev) => ({ text, n: prev.n + 1 }))
    setFindOpen(true)
  }

  /** Ctrl+F / Ctrl+H (⌘F / ⌘H on macOS) open Niobe's Find / Replace wherever focus is on the page —
   *  the nav, the top bar, the chat input, a slide's text box in edit mode — never the browser's
   *  find bar or History (AB#330). A window capture-phase listener, installed only while a part is
   *  open: with none (workspace home, admin pages) the browser keeps its own shortcuts. ⌘⌥F also
   *  opens Replace on macOS, where the desktop shell keeps ⌘H for the native Hide. A panel already
   *  open just switches mode in place, so the query/scope the member already typed survives;
   *  text selected in the part (prose or slide) seeds the query. */
  useEffect(() => {
    if (!room || !conn) return
    const onKey = (e: KeyboardEvent): void => {
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey) return
      const key = e.key.toLowerCase()
      // ⌥ turns the key into a symbol on macOS ('ƒ'): the physical key says which one it was.
      const macReplace = e.metaKey && e.altKey && e.code === 'KeyF'
      // Ctrl+Alt is AltGr on Windows: a character being typed, not a shortcut.
      if (!macReplace && (e.altKey || (key !== 'f' && key !== 'h'))) return
      e.preventDefault()
      const sel = window.getSelection()
      const inPart = !!sel && !sel.isCollapsed && !!sel.anchorNode && !!wrapRef.current?.contains(sel.anchorNode)
      const picked = inPart ? sel.toString().trim() : ''
      openFind(macReplace || key === 'h' ? 'replace' : 'find', picked.includes('\n') ? '' : picked)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [room, conn])

  return (
    <div className="canvasWrap" ref={wrapRef}>
      <article className="canvas">
        <div className={'doc-top' + (onToggleFocus ? ' lead-focus' : '')}>
          {/* Focus mode (AB#282, AB#313): leftmost, before the title slot, icon-only — the name says
              which way it goes. .lead-focus pulls the header's left padding in to the toolbar row's,
              so the icon sits right above that row's first control. */}
          {onToggleFocus && (
            <button
              type="button"
              className="doc-top-act focus-btn"
              data-help-id="canvas-focus"
              aria-label={focusMode ? t('focus_restore') : t('focus_expand')}
              title={focusMode ? t('focus_restore') : t('focus_expand')}
              aria-pressed={focusMode}
              onClick={onToggleFocus}
            >
              {focusMode ? <IconContract /> : <IconExpand />}
            </button>
          )}
          {room && activity ? (
            /* An agent is (or just was) editing this room: the sentence REPLACES the title (and the
               prose stats), and is not a button — no copy-link while it shows. Not a live region either: the polite
               announcer below already speaks the claim's arrival and release, once. */
            <span
              ref={activityRef}
              key="activity"
              className={`dt dt-activity${activity.phase === 'leaving' ? ' leaving' : ''}`}
              title={activityText}
            >
              {activitySentence}
            </span>
          ) : room ? (
            <button type="button" className="dt" onClick={copyPartLink} title={`${headerTitle} — ${t('part_link_copy')}`}>
              {headerTitle}
            </button>
          ) : (
            <span className="dt">No document selected</span>
          )}
          {/* Word/line counts are read off the part's Y.Text, which a grid room keeps empty — so a
              filled pricing grid would read "0 words · 0 lines". Better nothing than a wrong
              number; the grid states its own size in its footer. */}
          {/* An agent sentence takes the stats' place too, until it has landed in History. */}
          {room && conn && isProse && !activity && (
            <span className="docstats" aria-label="Statistics">
              {partStats.words} {t('stat_words')} · {partStats.lines} {t('stat_lines')}
              {docTotal && (
                <span className="docstats-doc">
                  {' · '}{t('stat_artefact')} {docTotal.words} {t('stat_words')} · {docTotal.lines} {t('stat_lines')}
                </span>
              )}
            </span>
          )}
          {/* Green + "Live" only when the room is genuinely connected AND synced. The dot is
              decorative: the label next to it carries the state (WCAG 1.4.1), so colour is never
              the only signal. */}
          {/* History and Export sit with the pill in one right-hand cluster — the one history
              surface and the one export entry point, the same for every part kind (prose, grid,
              slides all render through this Canvas). History comes FIRST, nearest the title slot,
              so a finished agent sentence has a short way to slide into it. */}
          <div className="doc-top-acts">
            <button
              ref={histBtnRef}
              className="doc-top-act hist-btn"
              title={t('history_title')}
              aria-haspopup="dialog"
              disabled={!conn}
              onClick={() => setHistoryOpen(true)}
            >
              <span aria-hidden="true">🕘</span>{' '}
              {/* Hidden visually on a narrow card (icon-only), never from the accessible name. */}
              <span className="hist-label">{t('history_btn')}</span>
              {/* Re-keyed on each landing so the pulse animation restarts. */}
              {history.length > 0 && (
                <span key={landed} className={`hist-count${landed > 0 ? ' land' : ''}`}>{history.length}</span>
              )}
            </button>
            <ExportMenu room={room} kind={kind} artefactType={artefactType} disabled={!conn} />
            <span className={`saved${room && !health.healthy ? ' warn' : ''}`}>
              <span
                className={`conn-dot${!room ? '' : health.healthy ? ' live' : ' warn'}`}
                aria-hidden="true"
              />
              {' '}
              {connLabel}
            </span>
            {chatReopen}
          </div>
        </div>

        {/* FORMATTING TOOLBAR — markdown editing, Word-like. None for a slides part: nothing in it
            applies to a deck (its own toolbar, .sl-bar, carries its controls), and an empty bar
            would draw a second line under .doc-top and be an empty toolbar for screen readers. */}
        {!isSlides && (
        <div className="fmtbar" ref={fmtbarRef} role="toolbar" aria-label="Text formatting">
          {/* The § Sections toggle comes FIRST: it is how a member finds their way around a long
              part, so it leads the bar rather than hiding among the view controls.
              A grid has no § — its content is not a heading tree — so the outline toggle would
              only ever open an empty panel. The backend answers the same way (GET /v1/sections
              returns []), so this hides a control rather than a capability. */}
          {isProse && (
            <div className="fmtgrp">
              <button
                className={`fmtbtn sec-toggle${outlineOpen ? ' on' : ''}`}
                title={t('sec_toggle')}
                aria-expanded={outlineOpen}
                aria-controls={OUTLINE_ID}
                disabled={!conn}
                onClick={() => setOutlineOpen((open) => !open)}
              >
                <span aria-hidden="true">☰</span> {t('sec_btn')}
              </button>
            </div>
          )}
          {/* Bold/italic/tables/images have no meaning in a grid — its own header row carries the
              controls that do (column name, column TYPE, add/remove row and column). */}
          {isProse && (
            <EditorToolbar
              editor={editor}
              bid={room ? room.split('/')[0] : null}
              readOnly={readOnly}
              room={room}
              beta={beta}
              admin={admin}
              onConverted={() => announce(t('grid_convert_done'))}
              announce={announce}
              // The artefact's anchors (AB#350): the Anchor button and the Link dialog's Anchor kind.
              ydoc={conn?.ydoc ?? null}
              wsUrl={wsUrl}
            />
          )}
          <div className="fmtgrp">
            <button className="fmtbtn" aria-label={t('zoom_out')} title={t('zoom_out')} disabled={zoom <= 0.5} onClick={() => setZoomClamped(zoom - 0.1)}>−</button>
            <button className="fmtbtn zoomlvl" aria-label={t('zoom_reset')} title={t('zoom_reset')} onClick={() => setZoomClamped(1)}>{Math.round(zoom * 100)}%</button>
            <button className="fmtbtn" aria-label={t('zoom_in')} title={t('zoom_in')} disabled={zoom >= 2} onClick={() => setZoomClamped(zoom + 0.1)}>+</button>
          </div>
          {/* Find / Replace: the bar's last group, at its right end — the same icon as the deck's bar
              (AB#330). */}
          <div className="fmtgrp fmt-end">
            <button
              className="fmtbtn"
              aria-label={t('fr_title_find')}
              title={`${t('fr_title_find')} (Ctrl+F)`}
              aria-haspopup="dialog"
              disabled={!conn}
              onClick={() => openFind('find')}
            >
              <IconSearch />
            </button>
          </div>
        </div>
        )}

        {/* One row per held §. Not a live region itself — the polite announcer below speaks
            arrivals and releases once, and two regions would say everything twice. */}
        {barLocks.length > 0 && (
          <div className="lockbar">
            <span className="ic" aria-hidden="true">◆</span>
            <ul className="lockrows">
              {/* Keyed on the lock's stable identity, not on the revision record that currently
                  expresses it: the console agent heartbeats its claim under a new uuid every
                  ~100s, and an id key made React tear the row down and build it again. */}
              {/* Human locks only — always § or slide scoped; agent claims (and every whole-part
                  claim) are reported in the header instead. */}
              {barLocks.slice(0, LOCKBAR_MAX).map((rev) => (
                <li key={lockKey(rev)}>{lockSentence(rev)}</li>
              ))}
              {barLocks.length > LOCKBAR_MAX && (
                <li className="lock-more">+{barLocks.length - LOCKBAR_MAX} {t('sec_more')}</li>
              )}
            </ul>
          </div>
        )}

        {/* Full-width disclosure panel: .workspace is a fixed 3-column grid with no room for a
            fourth rail, so a persistent outline rail is deferred. Zero layout change. Sticky
            under the toolbar, opened at most 25% of the editor's height, resizable by its bottom
            edge. */}
        {outlineOpen && (
          <SectionOutline
            id={OUTLINE_ID}
            editor={editor}
            locks={editorLocks}
            offset={sectionOffset}
            onCopyRef={copySectionRef}
            editorHeight={editorHeight}
          />
        )}

        {/* ONE visible explanation, rather than an editor that silently eats every keystroke.
            The server refuses a viewer's writes twice over already (REST 403 + a read-only CRDT
            connection); this is only so they know why. */}
        {/* The open part was reopened because its room was restored server-side (AB#124): what the
            member saw before was dropped, not merged. A status, so it is spoken once as it appears. */}
        {room && restoredNotice && (
          <p className="restored-banner" role="status">
            {t('room_restored_notice')}
          </p>
        )}
        {room && conn && readOnly && (
          <p className="readonly-banner" role="note">
            <span aria-hidden="true">🔒</span>{' '}
            {!partLock
              ? t('readonly_banner')
              : partLock.restoredFrom
                ? t('part_restored_banner')
                    .replace('{part}', () => partLock.restoredFrom!.part)
                    .replace('{time}', () => new Date(partLock.restoredFrom!.at).toLocaleString())
                : t('part_readonly_banner')}
          </p>
        )}

        {/* A slides part mounts OUTSIDE .paper: the prose typography there would restyle the
            slide's own paragraphs, and the paper's zoom would fight the canvas's own fit. Same
            room, presence, lock bar and history as any part — only the editor differs. */}
        {/* The ONE scrolling element of the editor (AB#234): everything above it stays put. */}
        <div className={`canvas-body${room && conn && !unopened && isSlides ? ' slides' : ''}`}>
        {room && conn && !unopened && isSlides ? (
          <Suspense
            fallback={
              <div className="paper">
                <p className="lede" role="status">
                  <span className="brand-spinner" aria-hidden="true" /> {t('sl_loading')}
                </p>
              </div>
            }
          >
            <SlidesEditor
              ydoc={conn.ydoc}
              room={room}
              bid={room.split('/')[0] ?? null}
              // Released (AB#186): any member who may edit the bid edits a deck — no beta gate.
              readOnly={readOnly}
              awareness={conn.awareness}
              actor={user.name}
              // A slide an AI claimed is read-only in the editor until the claim ends.
              revisions={workingLocks}
              synced={health.everSynced}
              focus={focusMode}
              releaseSignal={releaseSignal}
              onFindApi={setDeckFind}
              onOpenFind={() => openFind('find')}
              // The artefact's anchors (AB#350): listed and followed across its parts.
              wsUrl={wsUrl}
              onNotice={notify}
              // A bid template's publish (AB#454): the deck redraws, and says who published it.
              stateless={conn.provider}
            />
          </Suspense>
        ) : (
        <div className="paper" style={{ zoom }}>
          {/* THE ONE render decision in the app. A part's `kind` decides which editor opens on its
              room — prose in a Y.Text, or a structured grid in Y.Map('rows'). Everything around
              this branch (the connection, presence, the lock bar, the history log, export) is
              shared, because a grid part is a part: same room key, same ACL, same lifecycle. */}
          {!room || !conn ? (
            <p className="lede">Select a part in the left navigation to open it for co-editing.</p>
          ) : unopened ? (
            /* NOT the editor. Mounting it over a Y.Text that never synced renders '' — an empty
               document the member reads as an empty part, with a reassuring "Connecting…" chip
               above it and nothing else to go on. That is precisely how the desktop's expired-token
               bug presented: content silently gone, and no way to tell it apart from content that
               was never there. */
            <p className="lede" role="status">
              {health.authFailed ? t('conn_refused') : t('conn_opening')}
            </p>
          ) : kind === 'grid' ? (
            <GridEditor
              ydoc={conn.ydoc}
              awareness={conn.awareness}
              actor={user.name}
              // Beta: a non-tester may LOOK at an existing grid, never change it.
              readOnly={readOnly || !beta}
              revisions={workingLocks}
              releaseSignal={releaseSignal}
            />
          ) : (
            <RichEditor
              ytext={conn.ytext}
              ydoc={conn.ydoc}
              actor={user.name}
              suppressRef={aiApplying}
              awareness={conn.awareness}
              onEditor={setEditor}
              locks={editorLocks}
              sectionOffset={sectionOffset}
              connectionId={connectionId}
              onCopyRef={copySectionRef}
              onLockRejected={onLockRejected}
              readOnly={readOnly}
              releaseSignal={releaseSignal}
              // Ctrl/Cmd+click on a link to an anchor follows it, across the artefact's parts (AB#350).
              room={room}
              wsUrl={wsUrl}
              onNotice={notify}
            />
          )}
        </div>
        )}
        </div>

        {/* The member is looking at content we cannot vouch for.
            NOT a live region, and not role="status" either: the polite region below speaks the
            transition once, and a second region would say everything twice (role="status" exists
            precisely to create one, so pairing it with aria-live="off" contradicted itself).
            Placed LAST in .canvas, under the scrolling body (see .stalebar in styles.css), so
            appearing mid-typing shortens the body instead of shoving the editor — and the caret —
            down by its own height. */}
        {room && (health.stale || health.authFailed) && (
          <div className="stalebar">
            <span className="ic" aria-hidden="true">!</span>
            {/* `stale` needs a prior sync, so a room refused on its FIRST attempt used to show no
                banner at all — the worst case wearing the quietest face. */}
            <p>{health.stale ? t('conn_stale') : t('conn_refused')}</p>
          </div>
        )}

        {/* Lock notices (AB#301): last in .canvas, sticky at its foot like the stale banner. */}
        {room && conn && (
          <LockNotices
            notices={lockNotices.notices}
            onRelease={releaseLock}
            onDismiss={lockNotices.dismiss}
            slideName={slideLabel}
          />
        )}
      </article>

      <div className="visually-hidden" role="status" aria-live="polite">{announcement}</div>

      {/* Restore (in place) and Restore as part on every part kind — by entry, or by version on the
          restore points older than the log. Tags and comments: anyone but a viewer. Neither restore
          for a viewer, nor on a grid for a non-tester (the grid is read-only for them, and creating one is beta-gated).
          A read-only part keeps only Restore as part: an in-place restore is a write, a copy only
          reads. An entry with no restore point shows both disabled, with the reason. "Flatten
          history…": a bid admin or a tenant admin only (`admin`), hidden for everyone else. */}
      {historyOpen && (
        <HistoryModal
          entries={history}
          onClose={() => setHistoryOpen(false)}
          restorable={restorable}
          versions={roomVersions.versions}
          hasOlder={roomVersions.hasOlder}
          loadingOlder={roomVersions.loadingOlder}
          onShowOlder={roomVersions.loadOlder}
          loadDiff={loadVersionDiff}
          {...(!viewer ? { onUpdateVersion: updateVersion } : {})}
          {...(admin ? { onFlattenPreview: previewFlatten, onFlatten: flatten, locked: workingLocks.length > 0 } : {})}
          {...(!viewer && !readOnly && (kind !== 'grid' || beta)
            ? { onRestore: restoreInPlace, onRestoreVersion: restoreVersionInPlace }
            : {})}
          {...(!viewer && (kind !== 'grid' || beta)
            ? { onRestoreAsPart: restoreAsPart, onRestoreVersionAsPart: restoreVersionAsPart }
            : {})}
          {...(isSlides ? { slideName: historySlideName } : {})}
        />
      )}
      {findOpen && room && conn && conn.room === room && (
        <FindReplace
          mode={findMode}
          seed={findSeed}
          room={room}
          kind={kind}
          editor={editor}
          ydoc={conn.ydoc}
          ytext={conn.ytext}
          deck={isSlides ? deckFind : null}
          awareness={conn.awareness}
          synced={health.everSynced}
          wsUrl={wsUrl}
          actorName={user.name}
          readOnly={readOnly}
          applyingRef={aiApplying}
          onClose={() => setFindOpen(false)}
        />
      )}
      <Toasts toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((x) => x.id !== id))} />
    </div>
  )
}
